import { PayerAdjustmentGroupType } from '../../../api';
import {
    AdjustmentCodes,
    formatPayerAdjustmentGroupLabel,
    formatPayerAdjustmentGroupShortCode,
    isPayerAdjustmentGroupType,
    normalizePayerAdjustmentGroup,
} from '../../../services/dictionary';
import { StatementCodeKeyItem } from './statement-code-key';

export interface StatementAdjustmentRef {
    group?: string | PayerAdjustmentGroupType;
    reasonCode?: string;
}

const GROUP_SORT_ORDER: Record<string, number> = {
    CO: 0,
    PR: 1,
    OA: 2,
    PIR: 3,
    CR: 4,
};

const resolveGroupShortCode = (group?: string): string | undefined => {
    if (!group?.trim()) return undefined;
    const normalized = normalizePayerAdjustmentGroup(group);
    if (!isPayerAdjustmentGroupType(normalized)) return undefined;
    return formatPayerAdjustmentGroupShortCode(normalized);
};

const codeKeyTitle = (group: string, reason: string, description: string): string => {
    if (group === 'PR' && / Amount$/i.test(description)) {
        return description.replace(/ Amount$/i, '');
    }
    return formatPayerAdjustmentGroupLabel(group) || reason;
};

const codeKeyDescription = (reason: string): string => {
    const fromDictionary = AdjustmentCodes[reason as keyof typeof AdjustmentCodes];
    return (fromDictionary || reason).trim();
};

const codeKeyTone = (group: string): StatementCodeKeyItem['tone'] =>
    group === 'PR' ? 'warning' : 'neutral';

/** Unique group+reason pairs from claim payments, labeled via the CARC dictionary. */
export const buildStatementCodeKeyItems = (
    adjustments: StatementAdjustmentRef[],
): StatementCodeKeyItem[] => {
    const unique = new Map<string, StatementCodeKeyItem>();

    for (const adjustment of adjustments) {
        const reason = adjustment.reasonCode?.trim();
        const group = resolveGroupShortCode(adjustment.group);
        if (!reason || !group) continue;

        const code = `${group}-${reason}`;
        if (unique.has(code)) continue;

        const description = codeKeyDescription(reason);
        unique.set(code, {
            code,
            title: codeKeyTitle(group, reason, description),
            description,
            tone: codeKeyTone(group),
        });
    }

    return Array.from(unique.values()).sort((a, b) => {
        const [aGroup, aReason] = a.code.split('-');
        const [bGroup, bReason] = b.code.split('-');
        const groupDiff = (GROUP_SORT_ORDER[aGroup] ?? 99) - (GROUP_SORT_ORDER[bGroup] ?? 99);
        if (groupDiff !== 0) return groupDiff;
        return aReason.localeCompare(bReason, undefined, { numeric: true });
    });
};

export const collectAdjustmentsFromClaimPayments = (
    payments: Array<{
        adjustments?: StatementAdjustmentRef[];
        items?: Array<{ adjustments?: StatementAdjustmentRef[] }>;
    }>,
): StatementAdjustmentRef[] => {
    const collected: StatementAdjustmentRef[] = [];

    for (const payment of payments) {
        for (const adjustment of payment.adjustments || []) {
            collected.push(adjustment);
        }
        for (const item of payment.items || []) {
            for (const adjustment of item.adjustments || []) {
                collected.push(adjustment);
            }
        }
    }

    return collected;
};
